import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Heart, Droplet, Dumbbell, Brain, Check, Plus, ArrowLeft,
  Sun, Moon, Cloud, Users, User, Baby, Activity, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import MedicalDisclaimerBanner from '@/components/MedicalDisclaimerBanner';
import ContentCategoryLabel from '@/components/ContentCategoryLabel';
import AddFamilyMemberModal from '@/components/AddFamilyMemberModal';
import FamilyMemberCard from '@/components/FamilyMemberCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MICRO_HABITS = [
  { id: 'water', label: 'Drink Water', icon: Droplet, color: 'text-blue-600' },
  { id: 'meal', label: 'Eat Balanced Meal', icon: Heart, color: 'text-green-600' },
  { id: 'exercise', label: 'Move Your Body', icon: Dumbbell, color: 'text-orange-600' },
  { id: 'mental', label: 'Take Mental Pause', icon: Brain, color: 'text-purple-600' }
];

const UnifiedFamilyHealth = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [activeSection, setActiveSection] = useState('overview'); // overview, mens, womens, children
  
  // Daily tracking
  const [checkedHabits, setCheckedHabits] = useState([]);

  const loadDashboard = useCallback(async () => {
    try {
      // Auth via httpOnly cookie — no localStorage token needed.
      const response = await axios.get(`${API}/family-health/dashboard`, {
        withCredentials: true,
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error loading family dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTodayProgress = useCallback(() => {
    const today = new Date().toDateString();
    const savedHabits = localStorage.getItem(`habits_${today}`);
    if (savedHabits) setCheckedHabits(JSON.parse(savedHabits));
  }, []);

  // Guard against StrictMode dev double-invoke / accidental duplicate fires
  // within the same mount cycle. Resets on unmount/remount, so back-navigation
  // still triggers a fresh load.
  const didInitRef = useRef(false);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadDashboard();
    loadTodayProgress();
  }, [loadDashboard, loadTodayProgress]);

  const toggleHabit = (habitId) => {
    const today = new Date().toDateString();
    const updated = checkedHabits.includes(habitId)
      ? checkedHabits.filter(h => h !== habitId)
      : [...checkedHabits, habitId];
    
    setCheckedHabits(updated);
    localStorage.setItem(`habits_${today}`, JSON.stringify(updated));
    toast.success(updated.includes(habitId) ? 'Habit completed! 🎉' : 'Habit unchecked');
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading family health...</p>
        </div>
      </div>
    );
  }

  const members = dashboardData?.members || [];

  return (
    <div className="page-container bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-20 md:pb-6">
      <MedicalDisclaimerBanner page="family-health" />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4 text-gray-600"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">Family Health</h1>
            <ContentCategoryLabel category="PREVENTIVE" size="sm" />
          </div>
          <p className="text-gray-600 mb-6">
            Preventive health support for men, women, and children
          </p>

          {/* Section Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            <Button
              variant={activeSection === 'overview' ? 'default' : 'outline'}
              onClick={() => setActiveSection('overview')}
              className="whitespace-nowrap"
            >
              <Users className="h-4 w-4 mr-2" />
              Overview
            </Button>
            <Button
              variant={activeSection === 'mens' ? 'default' : 'outline'}
              onClick={() => setActiveSection('mens')}
              className="whitespace-nowrap"
            >
              <User className="h-4 w-4 mr-2" />
              Men's Health
            </Button>
            <Button
              variant={activeSection === 'womens' ? 'default' : 'outline'}
              onClick={() => setActiveSection('womens')}
              className="whitespace-nowrap"
            >
              <Heart className="h-4 w-4 mr-2" />
              Women's Health
            </Button>
            <Button
              variant={activeSection === 'children' ? 'default' : 'outline'}
              onClick={() => setActiveSection('children')}
              className="whitespace-nowrap"
            >
              <Baby className="h-4 w-4 mr-2" />
              Children's Health
            </Button>
            <Button
              variant={activeSection === 'maternal' ? 'default' : 'outline'}
              onClick={() => setActiveSection('maternal')}
              className="whitespace-nowrap"
            >
              <Heart className="h-4 w-4 mr-2 text-purple-600" />
              Maternal Health
            </Button>
          </div>
        </div>

        {/* Overview Section */}
        {activeSection === 'overview' && (
          <div className="space-y-6">
            {/* Greeting Card */}
            <Card className="bg-white/80 backdrop-blur-sm border-2 border-indigo-200">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {getGreeting()}, {user?.name || 'Friend'} 👋
                </h2>
                <p className="text-sm text-gray-600 italic">
                  "Small actions today can improve your wellbeing"
                </p>
              </CardContent>
            </Card>

            {/* Daily Habits */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Daily Habits</CardTitle>
                  <Badge variant="secondary">
                    {checkedHabits.length}/{MICRO_HABITS.length} completed
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {MICRO_HABITS.map(habit => {
                    const Icon = habit.icon;
                    const isChecked = checkedHabits.includes(habit.id);
                    
                    return (
                      <button
                        key={habit.id}
                        onClick={() => toggleHabit(habit.id)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                          isChecked
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className={`h-6 w-6 ${isChecked ? 'text-green-600' : habit.color}`} />
                        <span className={`text-sm font-medium text-center ${isChecked ? 'text-green-900' : 'text-gray-900'}`}>
                          {habit.label}
                        </span>
                        {isChecked && <Check className="h-4 w-4 text-green-600" />}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Family Members */}
            {members.length > 0 ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">
                    Family Members ({members.length})
                  </h3>
                  <Button variant="outline" size="sm" onClick={() => setShowAddMemberModal(true)}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Member
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {members.map(member => (
                    <FamilyMemberCard
                      key={member.id}
                      member={member}
                      onViewDetails={() => toast.info(`Viewing ${member.name}'s profile`)}
                      onDelete={loadDashboard}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <Card className="text-center py-12 border-2 border-dashed border-gray-300">
                <CardContent>
                  <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Start Your Family Health Journey
                  </h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    Add family members to track preventive health for everyone.
                  </p>
                  <Button onClick={() => setShowAddMemberModal(true)} size="lg">
                    <Plus className="h-5 w-5 mr-2" />
                    Add First Family Member
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Men's Health Section */}
        {activeSection === 'mens' && (
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-6 w-6 text-blue-600" />
                  Men's Health
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                  Daily tracking, cardiovascular, hormonal, sexual, mental, fitness,
                  preventive screenings, and age-based risk — all in one calm hub
                  built around male-specific health needs.
                </p>
                <Link
                  to="/mens-health"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl px-5 py-3 shadow-md transition-all"
                  data-testid="family-health-mens-hub-link"
                >
                  Open Men's Health hub
                  <span aria-hidden="true">→</span>
                </Link>
              </CardContent>
            </Card>
          </div>
        )}
        {/* Legacy Men's Health quick-tiles removed — superseded by the hub above. */}
        {false && (
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-6 w-6 text-blue-600" />
                  Men's Health
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Link to="/mens-health?section=daily" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Daily Health Tracking</h4>
                        <p className="text-sm text-gray-600 mb-3">Track stress, energy, and activity levels</p>
                        <div className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
                          Track Now →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/mens-health?section=cardiovascular" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Cardiovascular Health</h4>
                        <p className="text-sm text-gray-600 mb-3">Monitor blood pressure and heart health</p>
                        <div className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
                          Check Heart Health →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/mens-health?section=screening" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Preventive Screenings</h4>
                        <p className="text-sm text-gray-600 mb-3">Age-appropriate health checks and cancer screenings</p>
                        <div className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
                          Get Screening Plan →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/mens-health?section=mental" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Mental Wellness</h4>
                        <p className="text-sm text-gray-600 mb-3">Stress management and emotional health support</p>
                        <div className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
                          Check Wellness →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {/* Legacy Men's Health quick-tiles removed — superseded by the hub above. */}
        {false && (
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-6 w-6 text-blue-600" />
                  Men's Health
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Link to="/mens-health?section=daily" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Daily Health Tracking</h4>
                        <p className="text-sm text-gray-600 mb-3">Track stress, energy, and activity levels</p>
                        <div className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
                          Track Now →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/mens-health?section=cardiovascular" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Cardiovascular Health</h4>
                        <p className="text-sm text-gray-600 mb-3">Monitor blood pressure and heart health</p>
                        <div className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
                          Check Heart Health →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/mens-health?section=screening" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Preventive Screenings</h4>
                        <p className="text-sm text-gray-600 mb-3">Age-appropriate health checks and cancer screenings</p>
                        <div className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
                          Get Screening Plan →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/mens-health?section=mental" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Mental Wellness</h4>
                        <p className="text-sm text-gray-600 mb-3">Stress management and emotional health support</p>
                        <div className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
                          Check Wellness →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Women's Health Section */}
        {activeSection === 'womens' && (
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-pink-50 to-purple-100 border-pink-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="h-6 w-6 text-pink-600" />
                  Women's Health
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                  Cycle tracking, hormonal health, pregnancy & fertility, sexual,
                  mental, heart, preventive screenings, and life-stage guidance —
                  all in one calm, life-stage-aware hub.
                </p>
                <Link
                  to="/womens-health"
                  className="inline-flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white font-semibold rounded-xl px-5 py-3 shadow-md transition-all"
                  data-testid="family-health-womens-hub-link"
                >
                  Open Women's Health hub
                  <span aria-hidden="true">→</span>
                </Link>
              </CardContent>
            </Card>
          </div>
        )}
        {/* Legacy Women's Health quick-tiles removed — superseded by the hub above. */}
        {false && (
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-pink-50 to-purple-100 border-pink-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="h-6 w-6 text-pink-600" />
                  Women's Health
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Link to="/womens-health?section=menstrual" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Menstrual Health</h4>
                        <p className="text-sm text-gray-600 mb-3">Cycle tracking and reproductive wellness</p>
                        <div className="text-sm font-medium text-pink-600 hover:text-pink-700 flex items-center gap-1">
                          Track Cycles →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/womens-health?section=screening" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Preventive Screenings</h4>
                        <p className="text-sm text-gray-600 mb-3">Mammograms, cervical cancer, bone density</p>
                        <div className="text-sm font-medium text-pink-600 hover:text-pink-700 flex items-center gap-1">
                          Check Screening Needs →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/womens-health?section=pregnancy" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Pregnancy Planning</h4>
                        <p className="text-sm text-gray-600 mb-3">Pre-conception and prenatal wellness</p>
                        <div className="text-sm font-medium text-pink-600 hover:text-pink-700 flex items-center gap-1">
                          Start Planning →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/womens-health?section=hormonal" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Hormonal Health</h4>
                        <p className="text-sm text-gray-600 mb-3">Menopause support and hormone balance</p>
                        <div className="text-sm font-medium text-pink-600 hover:text-pink-700 flex items-center gap-1">
                          Check Hormonal Health →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Children's Health Section */}
        {activeSection === 'children' && (
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-green-50 to-teal-100 border-green-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Baby className="h-6 w-6 text-green-600" />
                  Children's Health
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Link to="/childrens-health" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Immunization Schedule</h4>
                        <p className="text-sm text-gray-600 mb-3">Track vaccines and immunization reminders</p>
                        <div className="text-sm font-medium text-green-600 hover:text-green-700 flex items-center gap-1">
                          View Schedule →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/childrens-health?tab=growth" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Growth & Development</h4>
                        <p className="text-sm text-gray-600 mb-3">Track milestones and developmental progress</p>
                        <div className="text-sm font-medium text-green-600 hover:text-green-700 flex items-center gap-1">
                          Track Growth →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/childrens-health?section=nutrition" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Nutrition Guidance</h4>
                        <p className="text-sm text-gray-600 mb-3">Age-appropriate meal plans and dietary tips</p>
                        <div className="text-sm font-medium text-green-600 hover:text-green-700 flex items-center gap-1">
                          Get Plan →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/childrens-health?section=mental" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Mental Wellness</h4>
                        <p className="text-sm text-gray-600 mb-3">Emotional health and behavioral support</p>
                        <div className="text-sm font-medium text-green-600 hover:text-green-700 flex items-center gap-1">
                          Track Wellness →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Maternal Health Section */}
        {activeSection === 'maternal' && (
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-purple-50 to-pink-100 border-purple-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="h-6 w-6 text-purple-600" />
                  Maternal Health
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Link to="/maternal-health" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Pregnancy Tracking</h4>
                        <p className="text-sm text-gray-600 mb-3">Week-by-week pregnancy journey</p>
                        <div className="text-sm font-medium text-purple-600 hover:text-purple-700 flex items-center gap-1">
                          Track Pregnancy →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/maternal-health?tab=appointments" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Prenatal Appointments</h4>
                        <p className="text-sm text-gray-600 mb-3">Schedule and track prenatal visits</p>
                        <div className="text-sm font-medium text-purple-600 hover:text-purple-700 flex items-center gap-1">
                          View Schedule →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/maternal-health?section=postpartum" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Postpartum Care</h4>
                        <p className="text-sm text-gray-600 mb-3">Recovery tracking and wellness support</p>
                        <div className="text-sm font-medium text-purple-600 hover:text-purple-700 flex items-center gap-1">
                          Track Recovery →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/maternal-health?section=nutrition" className="block">
                    <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                      <CardContent className="pt-6">
                        <h4 className="font-semibold mb-2">Nutrition Planning</h4>
                        <p className="text-sm text-gray-600 mb-3">Trimester-specific meal guidance</p>
                        <div className="text-sm font-medium text-purple-600 hover:text-purple-700 flex items-center gap-1">
                          Get Meal Plan →
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <AddFamilyMemberModal
        open={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        onSuccess={loadDashboard}
      />
    </div>
  );
};

export default UnifiedFamilyHealth;
